import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '../../../../../environments/environment';
import { SummarySendMail } from 'app/core/_base/crud/models/summary-send-mail-model';
import { SendInvoiceResponseDTO } from '../models/sendInvoice-model';
import { InvoiceLineDTO } from '../models/invoice-line-model';

/**
 * Class that handle the call to invoicing rest services
 * @author Castor Transformación Digital - Juan G. Pavas
 * @since 2019-10-31
 */
@Injectable({
    providedIn: 'root'
})
export class InvoicingService {

	private API_INVOICES_URL = environment.ipserver + 'o2c-backend/private/invoices/list';
    private API_UNPROCESSED_INVOICES_URL = environment.ipserver + 'o2c-backend/private/invoices/listUnprocessed';
    private API_GENERATE_PDF_URL = environment.ipserver + 'o2c-backend/private/invoices/generate';
    private API_GENERATE_PDF_SECOND_HOME_URL = environment.ipserver + 'o2c-backend/private/invoices/generateSecondHome';
    private API_UPDATE_STATUS_URL = environment.ipserver + 'o2c-backend/private/invoices/updateStatus';
    private API_SEND_EMAIL_URL = environment.ipserver + 'o2c-backend/private/invoices/sendEmail';
    private API_SEND_EMAIL_SECOND_HOME_URL = environment.ipserver + 'o2c-backend/private/invoices/sendSecondHomeEmail';
    private API_SEARCH_URL = environment.ipserver + 'o2c-backend/private/invoices/search';
    private API_LOGS_URL = environment.ipserver + 'o2c-backend/private/invoicelog/list';
    private API_LOGS_SEARCH_URL = environment.ipserver + 'o2c-backend/private/invoicelog/search';
    private API_GEMDBKEY_URL = environment.ipserver + 'o2c-backend/private/invoices/invoiceId';
    private API_OFFLINE_INVOICE_PDF = environment.ipserver + 'o2c-backend/private/invoices/generateOfflineInvoice';
	private API_SEND_INVOICE_URL = environment.ipserver + 'o2c-backend/private/invoices/sendInvoice';
	private API_SEND_MAIL_URL = environment.ipserver + 'o2c-backend/private/invoices/sendMail';
	private API_VALIDATE_INVOICE_EXISTS_URL = environment.ipserver + 'o2c-backend/private/invoices/validateInvoiceExists';
	constructor(
        private httpClient: HttpClient,
    ) { }

    /**
     * Method that obtains a list of invoices by calling invoices rest service
     * @author Castor Transformación Digital - Juan G. Pavas
     * @since 2019-10-31
     * @return Observable with a json that contains a list of invoices
     */
    public getAllInvoices(): Observable<any> {
        return this.getFilteredInvoices(this.API_INVOICES_URL);
    }

    /**
     * Method that obtains a list of unprocessed invoices by calling invoices rest service
     * @author Castor Transformación Digital - Juan G. Pavas
     * @since 2019-10-31
     * @return Observable with a json that contains a list of invoices
     */
    public getInvoicesUnprocessed(): Observable<any> {
        return this.getFilteredInvoices(this.API_UNPROCESSED_INVOICES_URL);
    }

    /**
     * Method responsible to cast the total currency (sopInvoiceTotalCurrency) to number
     * neccessary to sort the tables shown
     * @param api API to use
     *
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2019-12-6
     * @return Observable with a json that contains a list of invoices
     */
    private getFilteredInvoices(api: string): Observable<any> {
        return this.httpClient.get(api).pipe(
            map((value: Array<any>) => {
                value.forEach((element: { sopInvoiceTotalCurrency: any }) => {
                    element.sopInvoiceTotalCurrency = Number(element.sopInvoiceTotalCurrency.replace(/[^0-9.-]+/g, ''));
                });
                return value;
            })
        );
    }

    /**
     * Method to obtain a pdf invoice in base64 by calling invoices rest service
     * @author Castor Transformación Digital - Juan G. Pavas
     * @since 2019-10-31
     * @param invoices: list of invoices to print
     * @param toProcess: variable that indicates whether the invoice should be updated as processed
     * @return Observable with a text that contains a pdf invoice in base64
     */
    public generateInvoice(invoices: any[], toProcess: boolean, isRevised: boolean): Observable<any> {
        const invoicesBody = invoices.map(invoice => { return { gemDBKey: invoice.gemDBKey}});
        const paramsForReq = new HttpParams()
            .set('toProcess', String(toProcess))
            .set('isRevised', String(isRevised));
        const type = 'text' as 'json';
        return this.httpClient.post(this.API_GENERATE_PDF_URL, invoicesBody, { params: paramsForReq, responseType: type });
    }

    public generateSecondHomeInvoice(invoices: any[], toProcess: boolean, isRevised: boolean): Observable<any> {
        const invoicesBody = invoices.map(invoice => { return { gemDBKey: invoice.gemDBKey}});
        const paramsForReq = new HttpParams()
            .set('toProcess', String(toProcess))
            .set('isRevised', String(isRevised));
        const type = 'text' as 'json';
        return this.httpClient.post(this.API_GENERATE_PDF_SECOND_HOME_URL, invoicesBody, { params: paramsForReq, responseType: type });
    }


	/* Method to send invoice calling invoices rest service
	* @author Castor Transformación Digital - Alejandro Segura
	* @since 2021-10-14
	* @param invoices: list of invoices to print
	* @param toProcess: variable that indicates whether the invoice should be updated as processed
	* @return Observable with a text that contains a pdf invoice in base64
	*/
   public sendInvoice(invoices: any[], toProcess: boolean, isHold: boolean, isRevised: boolean, override: boolean,creditHoldOverride:boolean, lines:Array<InvoiceLineDTO>,isITSend: boolean,overrideInvoiceNumber: boolean): Observable<any> {
	   const invoicesBody = invoices.map(invoice => { return { gemDBKey: invoice.gemDBKey, invoiceLines : lines,invoiceNumber: invoice.invoiceNumber,newInvoiceNumber: invoice.newInvoiceNumber }});

	   const paramsForReq = new HttpParams()
		   .set('toProcess', String(toProcess))
		   .set('isRevised', String(isRevised))
		   .set('override', String(override))
		   .set('ishold', String(isHold))
           .set('isITSend', String(isITSend))
           .set('overrideInvoiceNumber', String(overrideInvoiceNumber))
		   .set('creditHoldOverride', String(creditHoldOverride));
	   const type = 'text' as 'json';
	   return this.httpClient.post<SendInvoiceResponseDTO>(this.API_SEND_INVOICE_URL, invoicesBody, { params: paramsForReq, responseType: type });
   }

    /**
     * Method to update the status of an invoices list
     * @author Castor Transformación Digital - Juan G. Pavas
     * @since 2019-10-31
     * @param invoices: list of invoices to update
     * @param newStatus: variable with the new status
     * @return Observable with a text that contains a message of confirmation
     */
    public updateStatusInvoices(invoices: any[], newStatus: any): Observable<any> {
        const paramsForReq = new HttpParams().set('newStatus', newStatus);
        const type = 'text' as 'json';
        return this.httpClient.put(this.API_UPDATE_STATUS_URL, invoices, { params: paramsForReq, responseType: type });
    }

    /**
     * Method to send an invoices list by email to the customer addresses
     * @author Castor Transformación Digital - Juan G. Pavas
     * @since 2019-10-31
     * @param invoices: list of invoices to send
     * @return Observable with a json that contains a list of messages and status by invoice
     */
    public sendMailInvoice(invoices: any[], toProcess: boolean, isHold: boolean, isRevised: boolean, override: boolean,creditHoldOverride:boolean, lines:Array<InvoiceLineDTO>): Observable<any>{
		const invoicesBody = invoices.map(invoice => { return { gemDBKey: invoice.gemDBKey, invoiceLines : lines }});

		const paramsForReq = new HttpParams()
			.set('toProcess', String(toProcess))
			.set('isRevised', String(isRevised))
			.set('override', String(override))
			.set('ishold', String(isHold))
			.set('creditHoldOverride', String(creditHoldOverride));
		const type = 'text' as 'json';
		return this.httpClient.post<SendInvoiceResponseDTO>(this.API_SEND_EMAIL_URL, invoicesBody, { params: paramsForReq, responseType: type });
    }

	/**
     * Method to send second home invoices list by email to the customer addresses
     * @author Implemented via AI
     * @since 2026-01-20
     * @param invoices: list of invoices to send
     * @return Observable with a json that contains a list of messages and status by invoice
     */
    public sendSecondHomeMailInvoice(invoices: any[], toProcess: boolean, isHold: boolean, isRevised: boolean, override: boolean,creditHoldOverride:boolean, lines:Array<InvoiceLineDTO>): Observable<any>{
		const invoicesBody = invoices.map(invoice => { return { gemDBKey: invoice.gemDBKey, invoiceLines : lines }});

		const paramsForReq = new HttpParams()
			.set('toProcess', String(toProcess))
			.set('isRevised', String(isRevised))
			.set('override', String(override))
			.set('ishold', String(isHold))
			.set('creditHoldOverride', String(creditHoldOverride));
		const type = 'text' as 'json';
		return this.httpClient.post<SendInvoiceResponseDTO>(this.API_SEND_EMAIL_SECOND_HOME_URL, invoicesBody, { params: paramsForReq, responseType: type });
    }

	/**
     * Method to send an invoices list by email to the customer addresses
     * @author Castor Transformación Digital - Juan G. Pavas
     * @since 2019-10-31
     * @param invoices: list of invoices to send
     * @return Observable with a json that contains a list of messages and status by invoice
     */
	 public sendMail(invoices: any[], toProcess: boolean, isHold: boolean, isRevised: boolean, override: boolean,creditHoldOverride:boolean, lines:Array<InvoiceLineDTO>): Observable<any>{
		const invoicesBody = invoices.map(invoice => { return { gemDBKey: invoice.gemDBKey, invoiceLines : lines }});

		const paramsForReq = new HttpParams()
			.set('toProcess', String(toProcess))
			.set('isRevised', String(isRevised))
			.set('override', String(override))
			.set('ishold', String(isHold))
			.set('creditHoldOverride', String(creditHoldOverride));
		const type = 'text' as 'json';
		return this.httpClient.post<SendInvoiceResponseDTO>(this.API_SEND_MAIL_URL, invoicesBody, { params: paramsForReq, responseType: type });


    }

    /**
     * Method that obtains a list of processed invoices logs by calling the invoice logs rest service
     * @author Castor Transformación Digital - Juan G. Pavas
     * @since 2019-10-31
     * @return Observable object that contains a list of logs in json format
     */
    public getAllInvoiceLogs(): Observable<any> {
        return this.httpClient.get(this.API_LOGS_URL);
    }

    /**
     * Method that obtains a list of processed invoices logs with search filters
     * by calling the invoice logs rest service
     * @author Castor Transformación Digital - Juan G. Pavas
     * @since 2019-12-03
     * @param filters: json with the search filters
     * @return Observable object that contains a list of logs in json format
     */
    public getInvoiceLogsSearch(filters: any): Observable<any> {
        const paramsForReq = new HttpParams()
            .set('division', filters.division)
            .set('docNumbers', filters.documentNumber)
            .set('startDate', filters.startDate)
            .set('endDate', filters.endDate)
            .set('customerNumber', filters.customerNumber)
			.set('docType', filters.docType);
        return this.httpClient.get(this.API_LOGS_SEARCH_URL, { params: paramsForReq });
    }

    /**
     * Method that obtains a list of processed invoices logs with search filters
     * by calling the invoice logs rest service
     * @author Castor Transformación Digital - Juan G. Pavas
     * @since 2019-12-03
     * @param filters: json with the search filters
     * @return Observable object that contains a list of logs in json format
     */
    public getInvoiceSearch(filters: any): Observable<any> {
        const paramsReq = new HttpParams()
            .set('division', filters.division)
            .set('poNumber', filters.poNumber)
            .set('salesOrder', filters.salesOrder)
            .set('invoiceNumber', filters.invoiceNumber)
            .set('debitNumber', filters.debitNumber)
            .set('creditNumber', filters.creditNumber)
            .set('customerNumber', filters.customerNumber)
            .set('startDate', filters.startDate)
            .set('endDate', filters.endDate)
            .set('status', filters.status);
        return this.httpClient.get(this.API_SEARCH_URL, { params: paramsReq })
            .pipe(
                map((value: Array<any>) => {
                    value.forEach((element: {
                        creditNoteNumber: number,
                        debitNoteNumber: number,
                        debitDescription: string,
                        totalCreditNoteValue: number,
                        sopInvoiceTotalCurrency: string,
                        customerNumber: string,
                        orderNumber: string,
                        orderNumbers: any[],
                        customerPO: string,
                        grandTotal: string,
                        creditNotes: any[]
                        debitNotes: any[]
                    }) => {
                        element.totalCreditNoteValue = 0;
                        element.customerPO = element.customerPO.trim();
                        element.customerNumber = element.customerNumber.trim();
                        if (element.creditNotes && element.creditNotes.length > 0) {
                            element.creditNoteNumber = element.creditNotes[0].creditNoteNumber;
							let totalCredit: number = 0;
							element.creditNotes.forEach(credit => {
								totalCredit = totalCredit + Number(credit.totalCreditNoteValue.replace(/[^0-9.-]+/g, '')) ;
							});
                            element.totalCreditNoteValue = - totalCredit;
                        }
                        if (element.debitNotes && element.debitNotes.length > 0) {
                            element.debitNoteNumber = element.debitNotes[0].transactionNumber;
                            element.totalCreditNoteValue = Number(
                                element.debitNotes[0].arTranTotalCurrency.replace(/[^0-9.-]+/g, '')) + element.totalCreditNoteValue;
                        }
                        // Calculating the grand total
                        // Invoice total - total credit + debit total
                        element.grandTotal = String(
                            Number(element.sopInvoiceTotalCurrency.replace(/[^0-9.-]+/g, '')) + element.totalCreditNoteValue
                        );
                        // getting gemkey to show the pdf
                        // if (element.customerNumber !== '' && element.customerPO !== '') {
                        //     this.saleOrderService.getgemDBKey(element.customerNumber, element.customerPO)
                        //         .subscribe(
                        //             data => {
                        //                 element.orderNumbers = data;
                        //             },
                        //         );
                        // }
                    });
                    return value;
                })
            );
    }

    /**
     * Obtain a gemDBKey to get the invoice file
     * @param invoiceNumber invoice number
     * @param division division
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-08-05
     */
    getGemDBKey(invoiceNumber: string, division: string): Observable<number> {
        const paramsReq = new HttpParams()
            .set('invoiceNumber', invoiceNumber)
            .set('division', division);
        return this.httpClient.get<number>(this.API_GEMDBKEY_URL, { params: paramsReq });
    }

    generateOfflineInvoice(invoiceId: number): Observable<string> {
        const paramsReq = new HttpParams()
            .set('invoiceId', invoiceId.toString());
        const type = 'text' as 'json';
        return this.httpClient.get<string>(this.API_OFFLINE_INVOICE_PDF, { params: paramsReq, responseType: type });
    }

    /**
     * Validates if an invoice exists in TUR_SOP_INV_HEADERS_SNAPSHOT by gemDBKey
     * @param gemDBKey The gemDBKey to validate
     * @return Observable with a boolean indicating if the invoice exists
     * @author Castor Transformación Digital
     */
    validateInvoiceExists(gemDBKey: number): Observable<boolean> {
        const paramsReq = new HttpParams()
            .set('gemDBKey', gemDBKey.toString());
        return this.httpClient.get<boolean>(this.API_VALIDATE_INVOICE_EXISTS_URL, { params: paramsReq });
    }
}
